import { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/auth/useAuth.js";

type Stage = "parsing" | "ready" | "error";

export default function AuthCallback(): JSX.Element {
  const { setSessionFromTokens } = useAuth();
  const loc = useLocation();
  const [stage, setStage] = useState<Stage>("parsing");
  const [msg, setMsg] = useState<string>("Verifico il link...");

  useEffect(() => {
    let cancelled = false;

    async function run() {
      // Supabase delivers tokens in the URL hash fragment:
      //   #access_token=...&refresh_token=...&token_type=bearer&expires_in=3600
      // We must parse manually because we set detectSessionInUrl: false.
      const hash = window.location.hash.startsWith("#") ? window.location.hash.slice(1) : window.location.hash;
      const params = new URLSearchParams(hash);
      const access_token = params.get("access_token");
      const refresh_token = params.get("refresh_token");
      const error_desc = params.get("error_description");

      if (error_desc) {
        if (cancelled) return;
        setStage("error");
        setMsg(error_desc);
        return;
      }
      if (!access_token || !refresh_token) {
        if (cancelled) return;
        setStage("error");
        setMsg("Link di accesso non valido o scaduto. Richiedi un nuovo link.");
        return;
      }

      const r = await setSessionFromTokens(access_token, refresh_token);
      if (cancelled) return;
      if (!r.ok) {
        setStage("error");
        setMsg(r.error ?? "Errore durante l'autenticazione.");
        return;
      }

      // Clear sensitive tokens from the URL hash.
      window.history.replaceState({}, "", window.location.pathname);
      setStage("ready");
    }

    void run();
    return () => { cancelled = true; };
  }, [setSessionFromTokens, loc.hash]);

  if (stage === "ready") return <Navigate to="/" replace />;

  return (
    <main className="grid min-h-screen place-items-center bg-bg px-6">
      <div className="w-full max-w-[420px] space-y-4 text-center">
        <p className="font-display text-h1 text-ink">
          av<span className="text-accent">/</span>estate
        </p>
        <p className={`font-mono text-meta uppercase tracking-wider ${stage === "error" ? "text-risk" : "text-ink-mute"}`}>
          {msg}
        </p>
        {stage === "error" ? (
          <a href="/login" className="inline-block text-meta font-mono uppercase tracking-wider text-accent hover:underline">
            ↶ torna al login
          </a>
        ) : null}
      </div>
    </main>
  );
}
